/**
 * Editorial articles for the blog. Static content that always renders, even
 * when the CMS database is unavailable. DB posts (see useBlogs) are merged in
 * ahead of these on the listing page.
 */

export type ArticleCategory = 'Colour' | 'How-to' | 'Exterior' | 'Waterproofing' | 'Trends' | 'Care' | 'News';

export type ArticleBlock =
  | { type: 'p'; text: string }
  | { type: 'h2'; text: string }
  | { type: 'quote'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'img'; src: string; alt: string; caption?: string };

export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  category: ArticleCategory;
  cover: string;
  coverAlt: string;
  author: string;
  /** ISO date */
  date: string;
  /** minutes */
  readingTime: number;
  content: ArticleBlock[];
  /** Catalog product routes worth linking from the article. */
  productRoutes?: string[];
}

export const ARTICLE_CATEGORIES: ArticleCategory[] = ['Colour', 'How-to', 'Exterior', 'Waterproofing', 'Trends', 'Care'];

const AUTHOR = 'Vertex Paints Team';

export const ARTICLES: Article[] = [
  {
    slug: 'exterior-paint-for-monsoon-regions',
    title: 'How to choose an exterior paint for monsoon-heavy regions',
    excerpt:
      'Three months of driving rain, high humidity and algae-friendly shade will find every weakness in a façade. Here is what actually matters when you pick an exterior emulsion for it.',
    category: 'Exterior',
    cover: '/images/web/auto/lattice-house-a.jpg',
    coverAlt: 'A modern house exterior with a lattice façade after rain',
    author: AUTHOR,
    date: '2026-06-02',
    readingTime: 6,
    productRoutes: ['/ExteriorWallPaint/UltraWRPExteriorEmulsion', '/ExteriorWallPaint/TitaniumOne', '/UnderCoat/BaseCoat', '/ExteriorWallPaint/FineSquareExteriorEmulsion'],
    content: [
      {
        type: 'p',
        text: 'In most of India the monsoon is the single hardest test an exterior coating faces. Water enters through hairline cracks and porous plaster, sits behind the paint film, and pushes it off from the inside. Humidity slows drying, shaded walls grow algae, and dissolved salts migrate to the surface as white efflorescence. A paint that looks perfect in April can blister by September if it was chosen for colour alone.',
      },
      { type: 'h2', text: 'Start with how water gets in' },
      {
        type: 'p',
        text: 'Rain rarely soaks through an intact paint film. It gets in at the edges: unfilled cracks, parapet tops, window sills, pipe penetrations and chalky old paint that has lost its bond. Before choosing a topcoat, walk the building after a shower and note where the plaster stays dark the longest. Those are the areas that need repair, not just repainting.',
      },
      { type: 'h2', text: 'What to look for on the tin' },
      {
        type: 'ul',
        items: [
          'Water repellency: a hydrophobic film makes rain bead and run off instead of soaking into plaster. This is the single most important property for wet climates.',
          'Crack bridging or film flexibility: masonry moves with temperature. A flexible emulsion stretches over hairline cracks rather than splitting with them.',
          'Anti-algal and anti-fungal additives: north-facing and shaded walls stay damp for days. Biocides in the film delay the black and green growth that follows.',
          'UV resistance: the sun that follows the rains is just as harsh. Look for colour-fast pigments and a binder that resists chalking.',
          'Breathability: a film that lets water vapour escape stops moisture trapped in the wall from blistering the paint.',
        ],
      },
      { type: 'h2', text: 'The system matters more than the topcoat' },
      {
        type: 'p',
        text: 'A premium exterior emulsion applied over unprimed, unrepaired plaster will fail early. Fill cracks with an exterior-grade filler, wash off algae and treat the surface with a fungicidal solution, then apply an exterior primer or base coat to seal the surface and control alkalinity. Only then does the topcoat get the chance to do its job.',
      },
      {
        type: 'quote',
        text: 'A water-repellent topcoat over a sealed, crack-free base is what keeps a façade dry. Skip either half and the other cannot compensate.',
      },
      { type: 'h2', text: 'Timing the job' },
      {
        type: 'p',
        text: 'Water-based exterior paints need dry weather to cure. Avoid painting when rain is expected within twenty-four hours, and stop work when the surface is wet or humidity is very high, because the film will not form properly. The best window in most regions is the dry stretch before the monsoon, giving the coating several weeks to harden before it faces heavy rain.',
      },
      { type: 'h2', text: 'Which Vertex product' },
      {
        type: 'p',
        text: 'Ultra WRP is built around a water-repellent system with a flexible, crack-resistant film and strong UV protection, which makes it our first recommendation for exposed, rain-facing elevations. Titanium One adds high opacity and a durable acrylic film for premium homes. On boundary walls and budget projects, Fine Square Exterior Emulsion gives dependable weather resistance. In every case, pair the topcoat with Vertex Base Coat and follow the coverage and dilution on the technical data sheet.',
      },
    ],
  },
  {
    slug: 'matt-soft-sheen-or-high-sheen',
    title: 'Matt, soft sheen or high sheen: which finish for which room',
    excerpt:
      'Sheen changes how a colour reads, how a wall wears and how easily it cleans. A room-by-room guide to choosing the right level of shine.',
    category: 'Colour',
    cover: '/images/web/living.jpg',
    coverAlt: 'A bright living room with natural light and soft painted walls',
    author: AUTHOR,
    date: '2026-05-12',
    readingTime: 5,
    productRoutes: ['/ExteriorWallPaint/SelectMattFinish', '/InteriorWallPaint/SilkShine_PearlGloss', '/InteriorWallPaint/EasyWashLuxuryEmulsion', '/InteriorWallPaint/CeilingWhite'],
    content: [
      {
        type: 'p',
        text: 'Sheen is simply the amount of light a dry paint film reflects. Matt reflects very little, high sheen reflects a lot, and soft sheen sits in between. That one property affects almost everything you will notice about the wall afterwards: how rich the colour looks, how visible the plaster underneath is, and how well the surface stands up to cleaning.',
      },
      { type: 'h2', text: 'Matt: soft, forgiving, calm' },
      {
        type: 'p',
        text: 'A matt finish scatters light, so undulations, filler marks and roller texture almost disappear. Colours look softer and more velvety. Traditionally matt paints were the least washable, though modern washable matts have closed much of the gap. Use matt on bedrooms, ceilings, feature walls and anywhere the plaster is less than perfect.',
      },
      { type: 'h2', text: 'Soft sheen: the everyday choice' },
      {
        type: 'p',
        text: 'Soft sheen, sometimes called satin or silk, has a gentle glow that brings depth to a colour without turning the wall into a mirror. It wipes clean far more easily than a flat finish and tolerates scuffs. This is the right default for living rooms, dining rooms, corridors, children’s rooms and most rental interiors.',
      },
      { type: 'h2', text: 'High sheen: hard-wearing, honest' },
      {
        type: 'p',
        text: 'Gloss and high-sheen films are the most durable and the easiest to wash, which is why they suit kitchens, bathrooms, utility areas, doors and trims. The trade-off is that a shiny surface shows every ripple in the wall, so the preparation has to be immaculate. Colours also read slightly deeper and cooler under a high sheen.',
      },
      { type: 'h2', text: 'Room by room' },
      {
        type: 'ul',
        items: [
          'Bedrooms: matt for a restful, low-glare wall.',
          'Living and dining rooms: soft sheen, or matt if the walls are a strong colour and you want it to stay soft.',
          'Kitchens: soft sheen or high sheen for grease and steam.',
          'Bathrooms: high sheen, applied over a sealed, moisture-tolerant base.',
          'Children’s rooms and corridors: a washable soft sheen that shrugs off handprints.',
          'Ceilings: a dedicated flat ceiling white that hides joints and reflects light evenly.',
        ],
      },
      { type: 'quote', text: 'Always look at a sample of the finish in the room’s own light. The same shade can look three tones different between matt and gloss.' },
      { type: 'h2', text: 'From the Vertex range' },
      {
        type: 'p',
        text: 'Easy Wash Luxury Emulsion is available in matt, soft sheen and high sheen, so one product can cover a whole home while changing finish room by room. Silk Shine Pearl Gloss gives a subtle pearl sheen for living spaces, and Ceiling White is formulated specifically for a bright, even overhead surface. Every one of them is tinted through Vertex Colour Mode, so the shade you sample is the shade you get.',
      },
    ],
  },
  {
    slug: 'why-primer-decides-how-long-paint-lasts',
    title: 'Why primer decides how long your paint lasts',
    excerpt:
      'The coat nobody sees does most of the work. What a primer actually does, when you can skip it, and why skipping it usually costs more paint, not less.',
    category: 'How-to',
    cover: '/images/web/hero-pastel.jpg',
    coverAlt: 'Smooth, evenly painted walls in a pastel interior',
    author: AUTHOR,
    date: '2026-04-21',
    readingTime: 5,
    productRoutes: ['/UnderCoat/VertexWaterThinnablePrimer', '/UnderCoat/FineSquareInteriorPrimer', '/UnderCoat/ultraWallSealer3IN1', '/UnderCoat/MG_RedOxide'],
    content: [
      {
        type: 'p',
        text: 'Ask why a paint job peeled after eighteen months and the answer is rarely the topcoat. It is almost always what happened underneath: bare plaster that drank the first coat unevenly, chalky old paint that never bonded, or fresh cement whose alkalinity attacked the film from below. A primer exists to solve exactly those problems.',
      },
      { type: 'h2', text: 'What a primer actually does' },
      {
        type: 'ul',
        items: [
          'Seals porosity so the wall absorbs the topcoat evenly instead of drinking it in patches.',
          'Promotes adhesion by giving the finish a surface it can grip, especially over old, glossy or powdery paint.',
          'Blocks alkalinity and efflorescence from new plaster and cement, which would otherwise burn and stain the topcoat.',
          'Improves coverage: a uniform white base means the finish colour reaches full opacity in fewer coats.',
          'On metal, an anti-corrosive primer is the layer that stops rust forming under the enamel.',
        ],
      },
      { type: 'h2', text: 'What skipping it looks like' },
      {
        type: 'p',
        text: 'Patchy sheen, where the wall looks glossier in some spots than others, is the classic sign of an unprimed surface. So is a colour that needs three or four coats to stop looking streaky. Peeling in sheets a year later, white salt blooms on exterior walls and rust bleeding through metal paint all trace back to the same missing layer.',
      },
      { type: 'quote', text: 'One coat of primer typically saves at least one coat of finish paint, and the finish coat is the expensive one.' },
      { type: 'h2', text: 'Choosing the right one' },
      {
        type: 'p',
        text: 'For interior and exterior masonry, a water-thinnable acrylic primer seals the surface and resists alkali. For old, chalky or patchy walls, a wall sealer penetrates and binds the loose surface before priming. Ferrous metal needs a red oxide, zinc chromate or zinc phosphate anti-corrosive primer; timber needs a PU primer surfacer under painted finishes. Never use an interior primer outside or a masonry primer on metal.',
      },
      { type: 'h2', text: 'When you can leave it out' },
      {
        type: 'p',
        text: 'If you are repainting a sound, previously painted wall in a similar colour, the existing paint may act as the primer, provided it is clean, dull and firmly bonded. Everywhere else, including new plaster, filled patches, colour changes from dark to light and any wall that has been putty-finished, prime first. Thin the primer as the technical data sheet directs, apply one full coat, and let it dry completely before the finish.',
      },
    ],
  },
  {
    slug: 'terrace-waterproofing-fix-the-cause',
    title: 'Terrace waterproofing: fix the cause, not the stain',
    excerpt:
      'A damp patch on the ceiling is the end of the story, not the beginning. How to trace a terrace leak to its source and build a waterproofing system that holds.',
    category: 'Waterproofing',
    cover: '/images/web/house-cutting-edge.jpg',
    coverAlt: 'A flat-roofed contemporary home with a large terrace',
    author: AUTHOR,
    date: '2026-03-30',
    readingTime: 7,
    productRoutes: ['/WaterProofing/RoofDampGuard', '/WaterProofing/SmartPolymer', '/WaterProofing/Procrete', '/WaterProofing/ReadyPlast'],
    content: [
      {
        type: 'p',
        text: 'Painting over a damp patch buys a few dry weeks and then the stain returns, usually larger. Water that reaches a ceiling has travelled: through a crack in the terrace screed, along a slab, and out at the weakest point below. Waterproofing that lasts starts on the roof, at the place the water gets in.',
      },
      { type: 'h2', text: 'Where terraces usually fail' },
      {
        type: 'ul',
        items: [
          'Ponding: water that stands for days because the slope towards the outlet is too shallow or has sagged.',
          'Cracked screed: thermal movement opens hairline cracks that widen a little every summer.',
          'Parapet junctions: the corner where floor meets wall is the most common entry point of all.',
          'Drain outlets and pipe penetrations: the seal around them shrinks and lets water straight through.',
          'Old bitumen or tar layers that have hardened, cracked and lost their bond.',
        ],
      },
      { type: 'h2', text: 'Diagnose before you coat' },
      {
        type: 'p',
        text: 'Inspect the terrace an hour after heavy rain and again the next morning. Mark where water still stands and where the surface stays dark. Push a screwdriver gently into suspect cracks and along the parapet coving: soft, crumbly mortar means the water has been there a while. Map these points against the damp patches inside and the source usually becomes obvious.',
      },
      { type: 'h2', text: 'The repair sequence' },
      {
        type: 'ul',
        items: [
          'Clean thoroughly: remove moss, loose screed and any failed coating, then let the surface dry.',
          'Open cracks into a shallow V-groove and fill them with a polymer-modified mortar so the repair bonds rather than sitting on top.',
          'Rebuild the coving where the floor meets the parapet so water is guided away from the joint.',
          'Correct the slope towards outlets if water ponds, using a ready plaster or screed.',
          'Apply a flexible waterproof coating in at least two coats, the second at right angles to the first, and carry it up the parapet well above the floor level.',
          'Seal around every outlet and pipe before the final coat.',
          'Allow full curing, then flood-test the terrace before the monsoon arrives.',
        ],
      },
      { type: 'quote', text: 'A waterproofing membrane is only as good as the corners. Floor-to-wall junctions and outlets deserve more care than the open slab.' },
      { type: 'h2', text: 'Keep the heat off too' },
      {
        type: 'p',
        text: 'A light-coloured, reflective roof coating lowers the surface temperature of a terrace on hot afternoons. That reduces the thermal expansion that keeps reopening cracks, and it makes the rooms below noticeably cooler. Cool Damp Proof combines that reflective finish with a damp-proof barrier designed for terraces and roof surfaces.',
      },
      { type: 'h2', text: 'A Vertex system for terraces' },
      {
        type: 'p',
        text: 'Use Smart Polymer for crack filling and to strengthen cementitious repairs, Procrete where the structure itself needs a cementitious waterproofing compound, Ready Plast to rebuild slopes and coving, and Cool Damp Proof as the final protective coat. Clear the drains every year before the rains and recoat at the interval on the technical data sheet, and the ceiling stays dry.',
      },
    ],
  },
  {
    slug: 'warm-neutrals-2026-interior-palette',
    title: 'Warm neutrals: the 2026 interior palette',
    excerpt:
      'Cool greys are giving way to sand, clay, oat and mushroom. How to build a warm neutral scheme that feels considered rather than beige.',
    category: 'Trends',
    cover: '/images/web/auto/tf-livingroom.jpg',
    coverAlt: 'A living room in warm, sandy neutral tones',
    author: AUTHOR,
    date: '2026-02-16',
    readingTime: 5,
    productRoutes: ['/InteriorWallPaint/EasyWashLuxuryEmulsion', '/InteriorWallPaint/FineSquareInterriorEmulsion', '/InteriorWallPaint/SilkShine_PearlGloss'],
    content: [
      {
        type: 'p',
        text: 'For most of the last decade the safe interior choice was a cool grey. In 2026 the mood has shifted decisively warmer: sandy off-whites, clay, oat, mushroom and soft terracotta. They pair naturally with timber, cane, linen and stone, and they make a room feel settled under both daylight and evening lamps.',
      },
      { type: 'h2', text: 'Why warm works' },
      {
        type: 'p',
        text: 'Warm neutrals carry a little red or yellow in their base. Under warm-white LED lighting, which most homes now use, a cool grey turns flat and slightly green, while a warm neutral simply glows. They also flatter skin tones and the natural materials that have become central to Indian interiors.',
      },
      { type: 'h2', text: 'Building the scheme' },
      {
        type: 'ul',
        items: [
          'Follow the 60-30-10 rule: a warm neutral on about sixty percent of surfaces, a second tone on thirty, and an accent on ten.',
          'Choose one warm white for ceilings and trims and keep it consistent through the house so rooms connect.',
          'Pick a mid-tone such as oat or mushroom for the main walls, and save the deepest tone for one wall, an alcove or the back of shelving.',
          'Add an accent with real colour: terracotta, olive, ink blue or a dusky pink all sit comfortably with warm neutrals.',
        ],
      },
      { type: 'h2', text: 'Watch the undertone' },
      {
        type: 'p',
        text: 'Two neutrals that look identical on a chip can pull in different directions on a full wall: one towards pink, the other towards yellow or green. Paint large samples on two walls, one facing the window and one away from it, and look at them in the morning and again at night before committing. North-facing rooms usually need a warmer, slightly deeper neutral than south-facing ones.',
      },
      { type: 'quote', text: 'The difference between beige and a good warm neutral is almost always the undertone, and you only see it at full scale.' },
      { type: 'h2', text: 'Finish and product' },
      {
        type: 'p',
        text: 'Warm neutrals look their best in matt or a soft sheen, which keeps the colour soft and lets texture from furnishings do the work. Easy Wash Luxury Emulsion in matt is the natural choice for living areas and bedrooms, and Silk Shine Pearl Gloss adds depth on a feature wall. Any of the shades in this palette can be tinted through Vertex Colour Mode, so bring a fabric swatch or a photograph to your dealer and match it exactly.',
      },
    ],
  },
  {
    slug: 'how-to-clean-and-maintain-painted-walls',
    title: 'How to clean and maintain painted walls',
    excerpt:
      'A good emulsion should look fresh for years, but only if it is cleaned the right way. Dusting, spot cleaning, stain removal and touch-ups without damaging the finish.',
    category: 'Care',
    cover: '/images/web/living-pink.jpg',
    coverAlt: 'A bright, well-kept living room with walls in a soft pastel shade',
    author: AUTHOR,
    date: '2026-01-19',
    readingTime: 4,
    productRoutes: ['/InteriorWallPaint/EasyWashLuxuryEmulsion', '/UnderCoat/ultraWallSealer3IN1', '/InteriorWallPaint/CeilingWhite'],
    content: [
      {
        type: 'p',
        text: 'Most damage to painted walls is not wear at all; it is over-enthusiastic cleaning. Scouring pads, strong chemicals and too much water strip sheen and leave dull patches that are far more visible than the mark you were trying to remove. A little routine care keeps the surface looking new for years.',
      },
      { type: 'h2', text: 'Let new paint cure first' },
      {
        type: 'p',
        text: 'A wall feels dry within hours, but the film keeps hardening for weeks. Avoid washing freshly painted walls for at least two to four weeks. Dust them gently if you must, but wait for full cure before any wet cleaning.',
      },
      { type: 'h2', text: 'Routine cleaning' },
      {
        type: 'ul',
        items: [
          'Dust walls every few weeks with a dry microfibre cloth or a soft brush, working from the top down.',
          'For general grime, mix a few drops of mild liquid soap in a bucket of warm water, wring a soft sponge almost dry and wipe in gentle circles.',
          'Rinse with a second sponge and clean water, then blot dry with a soft towel so no streaks form.',
          'Test any cleaning method on a hidden area first, behind a door or inside a cupboard.',
        ],
      },
      { type: 'h2', text: 'Dealing with specific marks' },
      {
        type: 'ul',
        items: [
          'Scuffs and fingerprints: a barely damp cloth is usually enough.',
          'Pencil and crayon: a soft eraser, or a paste of baking soda and water on a cloth, dabbed rather than rubbed.',
          'Grease near the cooker: warm water with a little dish soap, rinsed promptly.',
          'Mould spots: clean with a diluted mild disinfectant, then find and fix the moisture source, or the spots will return.',
        ],
      },
      { type: 'quote', text: 'Never use abrasive scrubbers, solvents or undiluted bleach on a tinted wall. They remove pigment as well as the stain.' },
      { type: 'h2', text: 'Touching up' },
      {
        type: 'p',
        text: 'Keep leftover paint sealed and labelled with the room and shade. For a touch-up, use the same product and sheen, apply with the same tool the wall was painted with so the texture matches, and feather the edges outwards. Small repairs blend far better on matt walls than on gloss, where a full wall-to-wall recoat is often the cleaner result.',
      },
      { type: 'h2', text: 'Choose a washable finish where it counts' },
      {
        type: 'p',
        text: 'In kitchens, corridors and children’s rooms, specify a washable emulsion from the start. Easy Wash Luxury Emulsion uses a polyurethane-based film that, applied over Vertex 3-in-1 Wall Sealer, can be cleaned with detergent without losing its finish. That single decision removes most of the anxiety from everyday cleaning.',
      },
    ],
  },
];

export const getArticle = (slug: string): Article | undefined => ARTICLES.find((a) => a.slug === slug);

export const relatedArticles = (article: Article, limit = 3): Article[] => {
  const same = ARTICLES.filter((a) => a.slug !== article.slug && a.category === article.category);
  const others = ARTICLES.filter((a) => a.slug !== article.slug && a.category !== article.category);
  return [...same, ...others].slice(0, limit);
};

export const formatArticleDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};
